import { useMemo, useState } from 'react';
import type { FilterGroup } from '@practium/ui';
import {
	COFFEE_LABELS,
	CROP_STAGE_LABELS,
	IRRIGATION_LABELS,
	PARAM_LABELS,
} from '../../constants/parameters.constants';
import { useTableList } from '../../hooks/useTableList';
import type {
	CoffeeType,
	PlanCropStage,
	PlanIrrigationSystem,
	PlanNutrient,
	PlanSectionProps,
} from '../../types/parameters.types';
import { setAtPath } from '../../utils/draftPath';
import { formatNumber } from '../../utils/format';
import { ValuesFormModal } from '../Forms/ValuesFormModal';
import { type ReferenceColumn } from '../Tables/ReferenceTable';
import { SectionList } from '../Tables/SectionList';
import tableStyles from '../Tables/ParamsTable.module.css';
import { NutrientRowsSection, type NutrientRow } from './NutrientRowsSection';

const COFFEE_TYPES = Object.keys(COFFEE_LABELS) as CoffeeType[];
const CROP_STAGES = Object.keys(CROP_STAGE_LABELS) as PlanCropStage[];

export const PlanRootSection = ({ plan, isSaving, onSave }: PlanSectionProps) => {
	const [editing, setEditing] = useState<CoffeeType | null>(null);
	const list = useTableList({ items: COFFEE_TYPES });
	const factors = plan.rootFactorByCultureAndCropStage;

	const columns = useMemo<ReferenceColumn<CoffeeType>[]>(
		() => [
			{
				key: 'coffee',
				header: 'Tipo de café',
				render: (coffee) => <span className={tableStyles.cellPrimary}>{COFFEE_LABELS[coffee]}</span>,
			},
			...CROP_STAGES.map((stage) => ({
				key: stage,
				header: CROP_STAGE_LABELS[stage],
				render: (coffee: CoffeeType) => (
					<span className={tableStyles.cellValue}>{formatNumber(factors[coffee][stage], 4)}</span>
				),
			})),
		],
		[factors]
	);

	return (
		<>
			<SectionList
				list={list}
				columns={columns}
				getRowId={(coffee) => coffee}
				unit={['tipo de café', 'tipos de café']}
				onEdit={setEditing}
			/>
			{editing && (
				<ValuesFormModal
					title={`Editar fator radicular: ${COFFEE_LABELS[editing]}`}
					sectionTitle="Fator por fase"
					fields={CROP_STAGES.map((stage) => ({ key: stage, label: CROP_STAGE_LABELS[stage] }))}
					initialValues={factors[editing]}
					isSaving={isSaving}
					onSubmit={(values) =>
						onSave(
							{ rootFactorByCultureAndCropStage: { ...factors, [editing]: values } },
							'Fator radicular salvo com sucesso!'
						)
					}
					onClose={() => setEditing(null)}
				/>
			)}
		</>
	);
};

export const PlanExtractionSection = (props: PlanSectionProps) => {
	const { plan } = props;
	const source = plan.nutrientExtractionByCultureAndCropStage;
	const rows = useMemo<NutrientRow[]>(
		() =>
			COFFEE_TYPES.flatMap((coffee) =>
				CROP_STAGES.map((stage) => ({
					id: `${coffee}-${stage}`,
					title: COFFEE_LABELS[coffee],
					meta: CROP_STAGE_LABELS[stage],
					values: source[coffee][stage],
					toUpdate: (values) => ({
						nutrientExtractionByCultureAndCropStage: setAtPath(source, [coffee, stage], values),
					}),
				}))
			),
		[source]
	);

	return (
		<NutrientRowsSection
			{...props}
			rows={rows}
			titleHeader="Tipo de café"
			metaHeader="Fase"
			unit={['combinação', 'combinações']}
		/>
	);
};

export const PlanExportsSection = (props: PlanSectionProps) => {
	const source = props.plan.nutrientExportsByCulture;
	const rows = useMemo<NutrientRow[]>(
		() =>
			COFFEE_TYPES.map((coffee) => ({
				id: coffee,
				title: COFFEE_LABELS[coffee],
				values: source[coffee],
				toUpdate: (values) => ({ nutrientExportsByCulture: { ...source, [coffee]: values } }),
			})),
		[source]
	);

	return <NutrientRowsSection {...props} rows={rows} titleHeader="Tipo de café" unit={['tipo de café', 'tipos de café']} />;
};

const EFFICIENCY_FILTERS: FilterGroup[] = [
	{
		label: 'Origem',
		options: [
			{ value: 'origin:soil', label: 'Solo' },
			{ value: 'origin:fertilizer', label: 'Fertilizante' },
		],
	},
];

export const PlanEfficiencySection = (props: PlanSectionProps) => {
	const soil = props.plan.soilNutrientEfficacyByIrrigationType;
	const fertilizer = props.plan.fertilizerNutrientEfficacyByIrrigationType;
	const rows = useMemo<NutrientRow[]>(
		() => [
			...(Object.keys(soil) as PlanIrrigationSystem[]).map((system) => ({
				id: `soil-${system}`,
				title: 'Solo',
				meta: IRRIGATION_LABELS[system],
				tags: ['origin:soil'],
				values: soil[system],
				toUpdate: (values: number[]) => ({
					soilNutrientEfficacyByIrrigationType: { ...soil, [system]: values },
				}),
			})),
			...(Object.keys(fertilizer) as (PlanIrrigationSystem | 'broad')[]).map((system) => ({
				id: `fertilizer-${system}`,
				title: 'Fertilizante',
				meta: IRRIGATION_LABELS[system],
				tags: ['origin:fertilizer'],
				values: fertilizer[system],
				isPercent: true,
				toUpdate: (values: number[]) => ({
					fertilizerNutrientEfficacyByIrrigationType: { ...fertilizer, [system]: values },
				}),
			})),
		],
		[fertilizer, soil]
	);

	return (
		<NutrientRowsSection
			{...props}
			rows={rows}
			titleHeader="Origem"
			metaHeader="Sistema"
			unit={['combinação', 'combinações']}
			filterGroups={EFFICIENCY_FILTERS}
			searchPlaceholder="Buscar por origem ou sistema..."
		/>
	);
};

export const PlanMinimumSection = (props: PlanSectionProps) => {
	const { nutrientOrder, nutrientMinimumFraction } = props.plan;
	const rows = useMemo<NutrientRow[]>(
		() => [
			{
				id: 'minimum',
				title: 'Fração mínima da demanda',
				values: nutrientOrder.map((nutrient) => nutrientMinimumFraction[nutrient]),
				isPercent: true,
				toUpdate: (values) => ({
					nutrientMinimumFraction: Object.fromEntries(
						nutrientOrder.map((nutrient, index) => [nutrient, values[index]])
					) as Record<PlanNutrient, number>,
				}),
			},
		],
		[nutrientMinimumFraction, nutrientOrder]
	);

	return <NutrientRowsSection {...props} rows={rows} titleHeader="Referência" unit={['linha', 'linhas']} />;
};

export const PlanNutrientsSection = ({ plan }: Pick<PlanSectionProps, 'plan'>) => {
	const list = useTableList({ items: plan.nutrientOrder, pageSize: plan.nutrientOrder.length });

	const columns = useMemo<ReferenceColumn<PlanNutrient>[]>(
		() => [
			{
				key: 'order',
				header: 'Ordem',
				render: (nutrient) => (
					<span className={tableStyles.cellSecondary}>{plan.nutrientOrder.indexOf(nutrient) + 1}</span>
				),
			},
			{
				key: 'plant',
				header: 'Nutriente da planta',
				alignEnd: true,
				render: (nutrient) => <span className={tableStyles.cellPrimary}>{PARAM_LABELS[nutrient] ?? nutrient}</span>,
			},
			{
				key: 'fertilizer',
				header: 'Nutriente do fertilizante',
				alignEnd: true,
				render: (nutrient) => (
					<span className={tableStyles.cellSecondary}>{plan.plantNutrientToFertilizerNutrient[nutrient]}</span>
				),
			},
		],
		[plan.nutrientOrder, plan.plantNutrientToFertilizerNutrient]
	);

	return (
		<SectionList list={list} columns={columns} getRowId={(nutrient) => nutrient} unit={['nutriente', 'nutrientes']} />
	);
};
